import { useState } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db8gmus6oi3g3tv8un50";

type Status = "idle" | "submitting" | "success" | "error";

export default function FeedbackForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    // 蜜罐校验：被填写则按普通成功反馈处理，不发送请求
    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("感谢你的反馈，我们已收到！");
      form.reset();
      return;
    }

    const email = String(formData.get("email") ?? "").trim();
    const content = String(formData.get("message") ?? "").trim();
    if (!email || !content) {
      setStatus("error");
      setMessage("请填写邮箱与反馈内容后再提交。");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const str = String(value).trim();
      if (str) payload.append(key, str);
    });

    setStatus("submitting");
    setMessage("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = `${serverMsg}`.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        setMessage("感谢你的反馈，我们已收到！");
        form.reset();
      } else {
        setStatus("error");
        setMessage(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <form
      data-readdy-form
      id="feedback-form"
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
        <i className="ri-chat-smile-2-line text-primary-500"></i>
        反馈与联系
      </h3>
      <p className="mt-2 text-sm text-foreground-600">
        有想看的题材、想吐槽的地方，或者合作意向？欢迎留言告诉我们。
      </p>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-foreground-700">昵称</span>
          <input
            type="text"
            name="nickname"
            placeholder="怎么称呼你"
            className="rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-accent-400 placeholder:text-foreground-400"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-foreground-700">邮箱</span>
          <input
            type="email"
            name="email"
            required
            placeholder="name@example.com"
            className="rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-accent-400 placeholder:text-foreground-400"
          />
        </label>
      </div>

      <label className="mt-4 flex flex-col gap-1.5">
        <span className="text-sm font-medium text-foreground-700">反馈类型</span>
        <select
          name="type"
          defaultValue="内容建议"
          className="cursor-pointer rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-accent-400"
        >
          <option value="内容建议">内容建议</option>
          <option value="功能反馈">功能反馈</option>
          <option value="内容纠错">内容纠错</option>
          <option value="商务合作">商务合作</option>
        </select>
      </label>

      <label className="mt-4 flex flex-col gap-1.5">
        <span className="text-sm font-medium text-foreground-700">反馈内容</span>
        <textarea
          name="message"
          required
          maxLength={500}
          rows={4}
          placeholder="请简要描述你的想法（最多 500 字）"
          className="resize-none rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-accent-400 placeholder:text-foreground-400"
        />
      </label>

      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="form-aux-field"
      />

      <div className="mt-5 flex flex-wrap items-center gap-4">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === "submitting" ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i> 提交中…
            </>
          ) : (
            <>
              <i className="ri-send-plane-line"></i> 提交反馈
            </>
          )}
        </button>
        {message && (
          <span
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm ${
              status === "success"
                ? "bg-accent-100 text-accent-800"
                : "bg-primary-100 text-primary-800"
            }`}
          >
            <i
              className={
                status === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"
              }
            ></i>
            {message}
          </span>
        )}
      </div>
    </form>
  );
}